import {Required} from 'utility-types'
import {CSSProperties, useEffect, useMemo, useRef, useState} from 'react'
import {useUnmount} from 'react-use'
import {
  selectTheme,
  selectProjectData,
  selectBatchData,
  createDispatchActions,
  selectUserDownload,
  ProjectDataDownloadFlowMetadataFetchResponsePayload,
} from '../../store'
import {DownloadIcon, DownloadGreyIcon, CheckSuccessIcon} from '../../asset/image'
import {RequestResult, RIF, t} from '../../lib'
import {IParticipant} from '../../shared/db'
import {useNavigate} from 'react-router-dom'
import {jobDataToUserDownloadInfo, useUserDownload, UserDownloadResultError, UserDownload} from '../../model'

import {PreparingDataView} from './preparing_data_view'
import {Button, ButtonReverse, ButtonText} from '../atoms'
import {createPortal} from 'react-dom'
import {JobState} from '../../shared/queue'
import {getProgressBarData} from '../../lib/data_download/progress'

interface DataDownloadButtonProps {
  onClick: () => void
  disabled: boolean
  inProgress?: boolean
  estimatedTimeRemaining?: string
}

const DataDownloadButton = (props: DataDownloadButtonProps) => {
  const {onClick, disabled, inProgress, estimatedTimeRemaining} = props

  const {color, fontWeight, pad} = selectTheme()

  return inProgress ? (
    <PreparingDataView estimatedTimeRemaining={estimatedTimeRemaining} onClick={onClick} />
  ) : (
    <ButtonReverse
      onClick={() => onClick()}
      disabled={disabled}
      btnPadding='medium'
      btnColor='primary'
      css={{position: 'relative'}}
      children={
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            color: disabled ? color.textIcon.disabled : color.primary,
            fontWeight: fontWeight.bold,
          }}>
          <img
            width='20'
            css={{marginRight: pad.small}}
            src={disabled ? DownloadGreyIcon : DownloadIcon}
            alt={'Download data icon'}
          />
          Download Data
        </div>
      }
    />
  )
}

// interface DataDownloadSectionContentV1Props {
//   downloadButtonDisabled: boolean
//   onClickDownloadButton: () => void
// }

// const DataDownloadSectionContentV1 = (props: DataDownloadSectionContentV1Props) => {
//   const {downloadButtonDisabled, onClickDownloadButton} = props

//   return (
//     <div>
//       <DataDownloadButton onClick={() => onClickDownloadButton()} disabled={downloadButtonDisabled} />
//     </div>
//   )
// }

interface DataDownloadSectionContentProps {
  projectId: string
  downloadButtonDisabled: boolean
  onClickDownloadButton: () => void
  noticePopupPlacement?: Required<Pick<CSSProperties, 'top' | 'right'>>
}

const METADATA_FETCH_INTERVAL = 3000

const DataDownloadSectionContent = (props: DataDownloadSectionContentProps) => {
  const {
    projectId,
    downloadButtonDisabled,
    onClickDownloadButton,
    noticePopupPlacement = {
      top: '110px',
      right: '30px',
    },
  } = props

  const {color, fontSize, fontWeight, borderRadius} = selectTheme()

  const {doREQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH, doUSER_DOWNLOAD_SET, doUSER_DOWNLOAD_DELETE} =
    createDispatchActions()

  const {
    isUserDownloadIdle,
    isUserDownloadInProgress,
    isUserDownloadSuccess,
    userDownloadInfo,
    downloaded,
    userDownload,
  } = useUserDownload(selectUserDownload()?.[projectId])
  const estimatedTimeRemaining = useMemo(() => {
    if (!isUserDownloadInProgress) return
    const {estimatedTimeRemainingInMs} = getProgressBarData(userDownload)
    return t.formatDuration(estimatedTimeRemainingInMs)
  }, [userDownload, isUserDownloadInProgress])

  const [showGoToDownloadPageNotice, setShowGoToDownloadPageNotice] = useState(false)

  const goToDownloadPageNoticeView = () => {
    return createPortal(
      <div
        css={{
          ...noticePopupPlacement,
          position: 'fixed',
          width: '360px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '24px',
          background: color.white,
          borderRadius: borderRadius.popup,
          boxShadow: '0px 4px 10px 0px rgba(104, 104, 104, 0.25)',
          zIndex: 999,
        }}>
        <img src={CheckSuccessIcon} width={24} />
        <p
          css={{
            fontWeight: fontWeight.thick,
            fontSize: fontSize.h5,
            color: color.textIcon.title,
          }}>
          Your file is ready to download
        </p>
        <p
          css={{
            fontWeight: fontWeight.medium,
            fontSize: fontSize.h6,
            color: color.textIcon.title,
          }}>
          We’ve finished preparing your data. Please go to the Data Download page to download your file.
        </p>
        <div
          css={{
            display: 'flex',
            gap: '8px',
          }}>
          <ButtonText
            textColor={color.textIcon.secondary}
            textColorWhileHovered={color.textIcon.secondary}
            backgroundColor={color.surface.default}
            backgroundColorWhileHovered={color.surface.hover.hoverGreyBg}
            onClick={() => setShowGoToDownloadPageNotice(false)}>
            Dismiss
          </ButtonText>
          <Button
            btnColor={'primary'}
            onClick={() => {
              setShowGoToDownloadPageNotice(false)
              onClickDownloadButton()
            }}>
            Go To Data Download Page
          </Button>
        </div>
      </div>,
      document.body,
    )
  }

  useEffect(() => {
    if (downloaded) {
      setShowGoToDownloadPageNotice(false)
      return
    }
    if (isUserDownloadSuccess) {
      setShowGoToDownloadPageNotice(true)
    }
  }, [isUserDownloadSuccess, downloaded])

  // api request
  const [requestResultOfProjectDataDownloadFlowMetadataFetch, setRequestResultOfProjectDataDownloadFlowMetadataFetch] =
    useState<RequestResult | null>(null)

  const nextMetadataFetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearNextMetadataFetchTimeout = () => {
    if (nextMetadataFetchTimeoutRef.current !== null) {
      clearTimeout(nextMetadataFetchTimeoutRef.current)
      nextMetadataFetchTimeoutRef.current = null
    }
  }

  const onDownloadUpdate = (userDownload: UserDownload) => {
    doUSER_DOWNLOAD_SET({
      ...userDownload,
    })
    clearNextMetadataFetchTimeout()
  }

  const onDownloadError = (userDownload: Required<Partial<UserDownload<UserDownloadResultError>>, 'result'>) => {
    console.error(`[DataDownload] ${JSON.stringify(userDownload.result)}`)
    if (userDownload.info) {
      doUSER_DOWNLOAD_SET({
        info: userDownload.info,
        result: userDownload.result,
        progress: userDownload.progress ?? null,
        startedAt: userDownload.startedAt ?? null,
      })
    }
  }

  const onDownloadTerminated = () => {
    doUSER_DOWNLOAD_DELETE({
      projectId,
    })
  }

  useEffect(() => {
    if (!requestResultOfProjectDataDownloadFlowMetadataFetch) return
    const {success, result} = requestResultOfProjectDataDownloadFlowMetadataFetch
    if (success) {
      const {
        payload: {job, lastUpdate},
      } = result as ProjectDataDownloadFlowMetadataFetchResponsePayload
      if (job?.isCancelled) {
        onDownloadTerminated()
        return
      } else if (job?.state === JobState.Failed) {
        onDownloadError({
          info: jobDataToUserDownloadInfo(job.data),
          result: {
            errorMessage: 'project data download failed',
          },
        })
      } else if (!job || job.state === JobState.Completed) {
        if (lastUpdate) {
          const {
            input,
            inputHash,
            url,
            createdAt,
            registeredAt,
            downloadedAt,
            failure,
            executionStart,
            isExpired,
            compressedSizeInBytes,
            uncompressedSizeInBytes,
            partsBreakdown,
            requester,
          } = lastUpdate
          if (isExpired) {
            onDownloadTerminated()
            return
          }

          const info = jobDataToUserDownloadInfo({
            input,
          })
          if (url) {
            onDownloadUpdate({
              info,
              result: {
                inputHash,
                url,
                createdAt,
                registeredAt,
                downloadedAt,
                executionStart,
                compressedSizeInBytes,
                uncompressedSizeInBytes,
                partsBreakdown,
                requester,
              },
              startedAt: registeredAt,
              progress: null,
            })
          } else {
            onDownloadError({
              info,
              result: {
                errorMessage: `project data download failed: ${failure}`,
              },
            })
          }
        }
      } else {
        onDownloadUpdate({
          info: jobDataToUserDownloadInfo(job.data),
          startedAt: new Date(job.data.registeredAt),
          result: null,
          progress: job.progress ? {...job.progress} : null,
        })
        clearNextMetadataFetchTimeout()
        nextMetadataFetchTimeoutRef.current = setTimeout(
          () => performDownloadFlowMetadataFetch(),
          METADATA_FETCH_INTERVAL,
        )
      }
    } else {
      onDownloadError({
        info: userDownloadInfo ?? undefined,
        result: {
          errorMessage: 'request project data download request failed',
        },
      })
    }
  }, [requestResultOfProjectDataDownloadFlowMetadataFetch])

  const performDownloadFlowMetadataFetch = () => {
    doREQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH({
      setRequestResult: setRequestResultOfProjectDataDownloadFlowMetadataFetch,
      payload: {
        projectId,
      },
      disableLoadingState: true,
    })
  }

  useEffect(() => {
    if (isUserDownloadIdle || isUserDownloadInProgress) {
      clearNextMetadataFetchTimeout()
      performDownloadFlowMetadataFetch()
    }
  }, [isUserDownloadIdle, isUserDownloadInProgress])

  useUnmount(() => {
    clearNextMetadataFetchTimeout()
  })

  return (
    <>
      {RIF(showGoToDownloadPageNotice, goToDownloadPageNoticeView())}
      <DataDownloadButton
        onClick={() => onClickDownloadButton()}
        disabled={downloadButtonDisabled}
        inProgress={isUserDownloadInProgress}
        estimatedTimeRemaining={estimatedTimeRemaining}
      />
    </>
  )
}

export interface DataDownloadSectionProps {
  projectId: string
  noticePopupPlacement?: DataDownloadSectionContentProps['noticePopupPlacement']
}

export const DataDownloadSection = (props: DataDownloadSectionProps) => {
  const {projectId, noticePopupPlacement} = props

  // extract data from store
  const {doREQUEST_BATCH_FETCH} = createDispatchActions()

  const navigate = useNavigate()

  const project = selectProjectData()?.[projectId]
  const batchId = project?.batchList?.[0]?.id
  const batchData = selectBatchData()
  const participantStateList: IParticipant[] = batchData?.[batchId]?.participantList || []
  const downloadButtonDisabled = participantStateList.length === 0

  useEffect(() => {
    if ((batchData && Object.keys(batchData).length !== 0) || !batchId) return
    doREQUEST_BATCH_FETCH({
      payload: {
        batchId,
      },
    })
  }, [batchData, batchId])

  const navigateToDataDownloadPage = () => {
    navigate(`/data_download/${projectId}`)
  }

  return (
    <div css={{display: 'flex', alignItems: 'center'}}>
      <DataDownloadSectionContent
        projectId={projectId}
        downloadButtonDisabled={downloadButtonDisabled}
        onClickDownloadButton={() => navigateToDataDownloadPage()}
        noticePopupPlacement={noticePopupPlacement}
      />
    </div>
  )
}
